import React from 'react';
import PageLoader from '../../../common/PageLoader';
import { applySort, SORT, useUserCoords } from '../../../../utils/sortListings';
import SortControl from '../../../common/SortControl';

import { Link } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import 'bootstrap/dist/css/bootstrap.css';
import { useTranslation } from 'react-i18next';
import ServiceFilterPanel, {
  EMPTY_SERVICE_FILTER,
  isServiceFilterActive,
  serviceMatchesFilter,
  serviceOptionsFromRows,
  brandOptionsFromRows,
} from '../../../common/ServiceFilterPanel';
import {
  getAirConditioningListing,
  getAirConditioningCategory,
  getFilteredAirConditioningListing,
} from './../../../../Redux/Actions/UserAction';

export default function AirConditioning() {
  const dispatch = useDispatch();
  const { t, i18n } = useTranslation();

  // Redux state selectors
  const home = useSelector((state) => state?.home);
  const AllList = home?.getAirConditioningList;
  const airConditioningCategory = home?.getCategoryList;
  const filteredListFromStore = home?.getFilteredAirConditioningList;

  // Local state
  const [categoryId, setCategoryId] = useState('');
  const [sortOption, setSortOption] = useState(SORT.DISTANCE); // [CF 2026-08-02] nearest-first by default
  // [CF 2026-08-02] Only asks the browser for a location when the user
  // actually picks the distance sort; cached for the session after that.
  const userCoords = useUserCoords(sortOption === SORT.DISTANCE);
  const [filteredAirConditioningList, setFilteredAirConditioningList] =
    useState(AllList);

  // Initial fetch of categories and listings
  useEffect(() => {
    dispatch(getAirConditioningCategory());
    dispatch(getAirConditioningListing());
  }, [dispatch]);

  // Update filtered list when Redux store updates
  useEffect(() => {
    setFilteredAirConditioningList(filteredListFromStore);
  }, [filteredListFromStore]);

  // Handle filter changes
  const onHandleChange = (e) => {
    setCategoryId(e.target.value);
  };

  // Dispatch filter action when categoryId changes
  useEffect(() => {
    if (categoryId !== 'default' && categoryId !== '') {
      dispatch(getFilteredAirConditioningListing(categoryId));
    } else {
      setFilteredAirConditioningList(AllList);
    }
  }, [AllList, categoryId, dispatch]);

  const [search, setSearch] = useState('');
  const [searchOpen, setSearchOpen] = useState(false);
  // [CF 2026-08-09 #14] Client asked for the same filter Garages and Quick
  // Services have. The A/C rows carry `services[].service`, `services[].brands`
  // and the `calendar`, so all three fields match in the browser exactly like
  // the garage panel does. NOTE the service NAMES come from the shared
  // `services` master, which currently holds only two placeholder rows
  // ("PAINT UPDATED" / "SSS UPDATED") — the control works, the vocabulary is
  // the client's to fix. Options are derived from the rows on screen so the
  // dropdown can never offer a value that returns nothing.
  const [svcFilter, setSvcFilter] = useState({ ...EMPTY_SERVICE_FILTER });
  const [filtersOpen, setFiltersOpen] = useState(false);
  const isRTL = (i18n.language || 'en').toLowerCase().startsWith('ar');

  const visibleList = React.useMemo(() => {
    if (!filteredAirConditioningList) return undefined;
    let list = [...filteredAirConditioningList];
    const q = search.trim().toLowerCase();
    if (q) {
      list = list.filter((x) =>
        (x.airConditioningName || '').toLowerCase().includes(q)
      );
    }
    list = list.filter((x) => serviceMatchesFilter(x, svcFilter));
    list = applySort(list, sortOption, {
      name: (x) => x.airConditioningName,
      coords: userCoords,
    });
    return list;
  }, [filteredAirConditioningList, sortOption, userCoords, search, svcFilter]);

  const serviceOptions = React.useMemo(
    () => serviceOptionsFromRows(filteredAirConditioningList, isRTL),
    [filteredAirConditioningList, isRTL]
  );
  const brandOptions = React.useMemo(
    () => brandOptionsFromRows(filteredAirConditioningList, isRTL),
    [filteredAirConditioningList, isRTL]
  );

  /* [CF 2026-08-17] Air Conditioning had no loading state — it rendered an empty
     grid until the rows arrived. `Spinner` was already imported but never used.
     Matches every other listing now. */
  if (filteredAirConditioningList === undefined) {
    return (
      <div className="text-center text-warning" style={{ padding: '80px' }}>
        <PageLoader />
      </div>
    );
  }

  return (
    <div class="main-content-area">
      <div class="container">
        <h2 class="mb-2">{t('Air Conditioning')}</h2>

        <div
          className="filter-row mb-4 d-flex align-items-center flex-nowrap"
          style={{ gap: '14px' }}
        >
          {/* Sort By — left */}
          <SortControl value={sortOption} onChange={setSortOption} />

          {/* [CF 2026-08-09 #14] Filter funnel — panel opens on its own row below. */}
          <button
            type="button"
            onClick={() => setFiltersOpen((o) => !o)}
            aria-label={t('Filter')}
            title={t('Filter')}
            style={{
              width: '42px',
              height: '42px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              border: 'none',
              borderRadius: '50%',
              background: isServiceFilterActive(svcFilter) ? '#fff7e6' : 'transparent',
              cursor: 'pointer',
              flex: '0 0 auto',
            }}
          >
            <img
              src="assets/images/icons/filter.png"
              alt={t('Filter')}
              style={{ width: '22px', height: '22px' }}
            />
          </button>

          {/* Search (icon → expands) + result count — fills remaining space on the same row */}
          <div
            className="d-flex align-items-center"
            style={{
              gap: '12px',
              minWidth: 0,
              flex: '1 1 auto',
              justifyContent: 'flex-end',
            }}
          >
            {searchOpen ? (
              <div
                style={{
                  position: 'relative',
                  flex: '1 1 auto',
                  maxWidth: '460px',
                  minWidth: 0,
                }}
              >
                <input
                  type="text"
                  autoFocus
                  className="form-control"
                  placeholder={t('Search by name')}
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  style={{ paddingInlineEnd: '34px' }}
                />
                <button
                  type="button"
                  onClick={() => {
                    setSearch('');
                    setSearchOpen(false);
                  }}
                  aria-label={t('Clear')}
                  style={{
                    position: 'absolute',
                    insetInlineEnd: '8px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    border: 'none',
                    background: 'transparent',
                    cursor: 'pointer',
                    fontSize: '20px',
                    lineHeight: 1,
                    color: '#666',
                  }}
                >
                  &times;
                </button>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => setSearchOpen(true)}
                aria-label={t('Search by name')}
                style={{
                  width: '42px',
                  height: '42px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  border: 'none',
                  borderRadius: '50%',
                  background: 'transparent',
                  cursor: 'pointer',
                }}
              >
                <img src="assets/images/icons/search.png" alt={t('Search by name')} style={{ width: '22px', height: '22px' }} />
              </button>
            )}
          </div>
        </div>

        {filtersOpen && (
          <ServiceFilterPanel
            value={svcFilter}
            onChange={setSvcFilter}
            onApply={() => setFiltersOpen(false)}
            fields={['service', 'brand', 'openNow']}
            serviceOptions={serviceOptions}
            brandOptions={brandOptions}
          />
        )}

        {visibleList && visibleList.length === 0 ? (
          <p className="text-muted">{t('No results match your search.')}</p>
        ) : (
          <div class="row">
            {visibleList && visibleList.length > 0 ? (
              (visibleList || []).map((item, i) => {
                const logo = item?.businessMedia?.find(
                  (media) => media?.mediaType === 'LOGO'
                )?.media?.path;
                return (
                  <div
                    key={item?.airConditioningId || i}
                    className="col-4 col-sm-4 col-md-4 col-lg-3 mb-4"
                  >
                    <div className="user-car-wash-block">
                      <Link
                        to={`/user-air-conditioning-details/${item?.airConditioningId}`}
                      >
                        <div
                          className="line-a"
                          style={{
                            width: '100%',
                            aspectRatio: '1 / 1',
                            overflow: 'hidden',
                            borderRadius: '50%',   // [CF] client: all logos rounded
                            background: '#f4f4f4',
                          }}
                        >
                          <img
                            style={{
                              width: '100%',
                              height: '100%',
                              objectFit: 'cover',
                            }}
                            src={logo || 'assets/images/user/taxi/user.jpg'}
                            alt={item?.airConditioningName || ''}
                            loading="lazy"
                            decoding="async"
                            onError={(e) => {
                              e.target.onerror = null;
                              e.target.src = 'assets/images/user/taxi/user.jpg';
                            }}
                          />
                        </div>
                        <div className="line-b">
                          {item?.airConditioningName}
                        </div>
                      </Link>
                    </div>
                  </div>
                );
              })
            ) : (
              <div
                className="text-center"
                style={{
                  padding: '50px',
                }}
              >
                <h4>No Air Conditioning Service found</h4>
                <p>
                  No Air Conditioning Service is available right now. Please
                  visit later.
                </p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
